/**
 * CatalogIndex
 *
 * In-memory search index over the user's catalog packs and team folder assets,
 * scoped to the active team. Singleton that refreshes when the catalog auth
 * token or active team changes.
 */

import type { BehaviorData } from 'trace-viewer/behaviors';
import { Catalogs, Teams, Folders } from 'trace-viewer/cms';
import {
    apiConfig, resolveUrl, getCatalogToken, onCatalogTokenChange,
    LS_TEAM_ID_KEY, getCatalogSelectedFolderId,
} from '../catalog-shared';
import { LS_REFRESH_TOKEN_KEY } from '../catalog-auth';
import { parseCmsMetadata } from 'trace-viewer/editor';

/** Unified asset entry in the search index. */
export interface IndexedAsset {
    /** Unique identifier (assetId from API). */
    id: string;
    /** Display title. */
    title: string;
    /** Original filename (useful for search). */
    filename: string;
    /** Resolved download URL. */
    url: string;
    /** Resolved thumbnail URL (if available). */
    thumbnailUrl?: string;
    /** Source: catalog pack name or team folder path. */
    source: string;
    /** Source type: 'catalog' or 'folder'. */
    sourceType: 'catalog' | 'folder';
    /** Folder ID (for team folder assets). */
    folderId?: string;
    /** Folder title (for team folder assets). */
    folderTitle?: string;
    /** Entity type (Model, Image, Video, etc.). */
    entityType?: string;
    /** Behaviors parsed from CMS metadata. */
    behaviors?: BehaviorData[];
}

/** Summary of a team folder for the AI agent. */
export interface FolderInfo {
    folderId: string;
    title: string;
    isDefault: boolean;
    isOpen: boolean;
    assetCount: number;
}

class CatalogIndexService {
    private _assets: IndexedAsset[] = [];
    private _folders: FolderInfo[] = [];
    private _status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
    private _lastToken: string | null = null;
    private _lastTeamId: string | null = null;
    private _loadPromise: Promise<void> | null = null;

    constructor() {
        onCatalogTokenChange((token) => {
            // Skip guest tokens — they have no team and can't access /teams/{id}/folders.
            // The presence of a refresh token in localStorage is the signal for "real session".
            const hasRealSession = localStorage.getItem(LS_REFRESH_TOKEN_KEY) !== null;
            if (token && hasRealSession) {
                void this._buildIndex(token);
            } else {
                this._assets = [];
                this._folders = [];
                this._status = 'idle';
                this._lastToken = null;
                this._lastTeamId = null;
            }
        });
    }

    get isAvailable(): boolean {
        return this._status === 'ready' && this._assets.length > 0;
    }

    get status(): string {
        return this._status;
    }

    get assetCount(): number {
        return this._assets.length;
    }

    /** Ensure the index is built for the current token and active team. */
    async ensureLoaded(): Promise<void> {
        const token = getCatalogToken();
        if (!token) {
            throw new Error('Not logged into catalog. Please sign in via the Catalog panel first.');
        }
        const teamId = localStorage.getItem(LS_TEAM_ID_KEY);
        if (!teamId) {
            throw new Error('No team selected. Please select a team in the Catalog panel first.');
        }
        const needsRebuild = this._status !== 'ready'
            || this._lastToken !== token
            || this._lastTeamId !== teamId;
        if (!needsRebuild) return;
        if (this._status === 'loading' && this._loadPromise) {
            await this._loadPromise;
            // After awaiting, check if the team changed while we waited
            if (this._lastTeamId !== teamId) {
                await this._buildIndex(token);
            }
            return;
        }
        await this._buildIndex(token);
    }

    /** Get the folder list for the active team. */
    getFolders(): FolderInfo[] {
        return this._folders.map(f => ({
            ...f,
            isOpen: f.folderId === getCatalogSelectedFolderId(),
        }));
    }

    /** Get assets in a specific folder. */
    getByFolder(folderId: string): IndexedAsset[] {
        return this._assets.filter(a => a.folderId === folderId);
    }

    /** Search the index by keyword(s). */
    search(query: string, limit = 20): IndexedAsset[] {
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length === 0) return this._assets.slice(0, limit);

        const scored = this._assets
            .map(asset => {
                const searchText = `${asset.title} ${asset.filename} ${asset.source} ${asset.entityType ?? ''}`.toLowerCase();
                let score = 0;
                for (const term of terms) {
                    if (searchText.includes(term)) score++;
                }
                return { asset, score };
            })
            .filter(s => s.score > 0)
            .sort((a, b) => b.score - a.score);

        return scored.slice(0, limit).map(s => s.asset);
    }

    /** Get an asset by ID (for import_asset tool). */
    getById(assetId: string): IndexedAsset | undefined {
        return this._assets.find(a => a.id === assetId);
    }

    /** Get an asset whose URL contains the given substring (e.g. a blob UUID). */
    getByUrlSubstring(sub: string): IndexedAsset | undefined {
        return this._assets.find(a => a.url.includes(sub));
    }

    private async _buildIndex(token: string): Promise<void> {
        this._status = 'loading';
        this._lastToken = token;
        this._lastTeamId = localStorage.getItem(LS_TEAM_ID_KEY);

        const promise = this._doBuildIndex(token, this._lastTeamId!);
        this._loadPromise = promise;
        try {
            await promise;
        } finally {
            this._loadPromise = null;
        }
    }

    private async _doBuildIndex(token: string, teamId: string): Promise<void> {
        const assets: IndexedAsset[] = [];
        const folderInfos: FolderInfo[] = [];

        try {
            const config = apiConfig(token);
            const catalogsClient = new Catalogs(config);
            const teamsClient = new Teams(config);
            const foldersClient = new Folders(config);

            // Fetch catalog packs and team folders in parallel
            const [packsRes, foldersRes] = await Promise.all([
                catalogsClient.packsList(),
                teamsClient.foldersList(teamId),
            ]);

            // ── Catalog packs ─────────────────────────────────────
            const packs = packsRes.data ?? [];

            const fullPacks = await Promise.all(
                packs.map(async (pack) => {
                    if (pack.catalogAssets && pack.catalogAssets.length > 0) return pack;
                    if (!pack.id) return pack;
                    try {
                        const detail = await catalogsClient.packsDetail(pack.id);
                        return detail.data;
                    } catch {
                        return pack;
                    }
                }),
            );

            for (const pack of fullPacks) {
                for (const asset of pack.catalogAssets ?? []) {
                    if (!asset.assetId || !asset.url) continue;
                    assets.push({
                        id: asset.assetId,
                        title: asset.title ?? '',
                        filename: asset.originalFilename ?? '',
                        url: resolveUrl(asset.url) ?? asset.url,
                        thumbnailUrl: resolveUrl(asset.thumbnailUrl) ?? undefined,
                        source: pack.name ?? 'Catalog',
                        sourceType: 'catalog',
                        entityType: asset.entityType ?? undefined,
                        behaviors: parseCmsMetadata(asset.metadata),
                    });
                }
            }

            // ── Team folders ──────────────────────────────────────
            const folders = foldersRes.data?.folders ?? [];

            // Fetch all folder assets in parallel
            const folderResults = await Promise.all(
                folders.map(async (folder) => {
                    if (!folder.folderId) return { folder, assets: [] };
                    try {
                        const res = await foldersClient.assetsList(folder.folderId);
                        return { folder, assets: res.data ?? [] };
                    } catch {
                        return { folder, assets: [] };
                    }
                }),
            );

            for (const { folder, assets: folderAssets } of folderResults) {
                if (!folder.folderId) continue;

                folderInfos.push({
                    folderId: folder.folderId,
                    title: folder.title ?? 'Untitled',
                    isDefault: folder.isDefault ?? false,
                    isOpen: folder.folderId === getCatalogSelectedFolderId(),
                    assetCount: folderAssets.length,
                });

                for (const asset of folderAssets) {
                    if (!asset.assetId || !asset.url) continue;
                    assets.push({
                        id: asset.assetId,
                        title: asset.title ?? '',
                        filename: asset.originalFilename ?? '',
                        url: resolveUrl(asset.url) ?? asset.url,
                        thumbnailUrl: resolveUrl(asset.thumbnailUrl) ?? undefined,
                        source: folder.title ?? 'Folder',
                        sourceType: 'folder',
                        folderId: folder.folderId,
                        folderTitle: folder.title ?? 'Untitled',
                        entityType: asset.entityType ?? undefined,
                        behaviors: parseCmsMetadata(asset.metadata),
                    });
                }
            }

            this._assets = assets;
            this._folders = folderInfos;
            this._status = 'ready';
            console.log(`[CatalogIndex] Indexed ${assets.length} assets in ${folderInfos.length} folders (team ${teamId})`);
        } catch (err) {
            this._status = 'error';
            console.error('[CatalogIndex] Failed to build index:', err);
            throw err;
        }
    }
}

/** Singleton instance. */
export const catalogIndex = new CatalogIndexService();
